import {FC, JSX, MouseEventHandler, useCallback} from 'react';
import {styled, useTheme} from 'styled-components';

import {DAYS_IN_WEEK, startOfLocalDay, startOfUtcDay} from '@shared/lib/utils/date_utils';

import {withThemeOverride} from '@shared_web/components/core/_base';
import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {chevronLeftIcon} from '@shared_web/components/old_icons/chevron_left_icon';
import {chevronRightIcon} from '@shared_web/components/old_icons/chevron_right_icon';
import {EmptyFragment} from '@shared_web/lib/react';
import {optional} from '@shared_web/lib/styled_utils';

interface CalendarProps {
  month: number;
  year: number;
  isDisabled?: (date: Date) => boolean;
  renderCell?: (date: Date) => JSX.Element;
  onPreviousClick?: () => void;
  onNextClick?: () => void;
  onDayClick?: (date: Date) => void;
  utc?: boolean;
  denseUi?: boolean;
  locale?: string;
}

const MONDAY = 1;
const firstDayOfTheWeek = MONDAY;

export function calendarWeeks(opts: {month: number; year: number; utc: boolean}): Date[][] {
  const {month, year, utc} = opts;

  // Compute start
  const startOfMonth = utc ? new Date(Date.UTC(year, month, 1)) : new Date(year, month, 1);
  const startOfMonthDay = utc ? startOfMonth.getUTCDay() : startOfMonth.getDay();

  const calendarStart = new Date(startOfMonth);
  const dateStartDelta = (startOfMonthDay + DAYS_IN_WEEK - firstDayOfTheWeek) % DAYS_IN_WEEK;
  if (utc) {
    calendarStart.setUTCDate(calendarStart.getUTCDate() - dateStartDelta);
  } else {
    calendarStart.setDate(calendarStart.getDate() - dateStartDelta);
  }

  // Compute weeks
  const weeks: Date[][] = [];
  const endOfMonth = new Date(year, month + 1, 0);
  let current = calendarStart;
  while (current.getTime() <= endOfMonth.getTime()) {
    const week: Date[] = [];
    for (let i = 0; i < DAYS_IN_WEEK; i++) {
      week.push(current);
      const date = new Date(current);
      if (utc) {
        date.setUTCDate(date.getUTCDate() + 1);
      } else {
        date.setDate(date.getDate() + 1);
      }
      current = date;
    }
    weeks.push(week);
  }

  return weeks;
}

const _Calendar: FC<CalendarProps> = props => {
  const {
    renderCell: renderCellProp,
    year,
    month,
    onPreviousClick,
    onNextClick,
    onDayClick,
    isDisabled,
    utc = false,
    denseUi = false,
    locale,
  } = props;
  const theme = useTheme();

  const handlePreviousClick = onPreviousClick;
  const handleNextClick = onNextClick;

  const handleDayClick = useCallback<MouseEventHandler>(
    evt => {
      const date = new Date(Number.parseFloat(evt.currentTarget.getAttribute('data-ts') ?? ''));
      if (Number.isNaN(date.getTime())) {
        return;
      }
      onDayClick?.(date);
    },
    [onDayClick]
  );

  const renderCell = useCallback(
    (date: Date): JSX.Element => {
      if (renderCellProp) {
        return renderCellProp(date);
      }
      const today = utc ? startOfUtcDay() : startOfLocalDay();
      const day = utc ? startOfUtcDay(date) : startOfLocalDay(date);
      const isToday = today.getTime() === day.getTime();
      const disabled = isDisabled ? isDisabled(date) : false;

      return (
        <CalendarCell $disabled={disabled} $highlighted={false} $denseUi={denseUi}>
          <CalendarCellDay>
            <CalendarCellDayNumber $today={isToday} $denseUi={denseUi}>
              {date.getDate()}
            </CalendarCellDayNumber>
          </CalendarCellDay>
        </CalendarCell>
      );
    },
    [isDisabled, renderCellProp, utc, denseUi]
  );

  const weeks = calendarWeeks({month, year, utc});
  const [firstWeek] = weeks;
  if (!firstWeek) {
    return EmptyFragment;
  }

  return (
    <CalendarTable>
      <CalendarHeader>
        <tr>
          <td colSpan={firstWeek.length}>
            <MonthYear>
              <SvgWrapper onClick={handlePreviousClick}>
                <SvgIcon icon={chevronLeftIcon} color={theme.main?.textColor} size={13} />
              </SvgWrapper>
              <CalendarHeaderValue>
                {utc
                  ? new Date(Date.UTC(year, month)).toLocaleString(locale, {
                      month: 'long',
                      year: 'numeric',
                    })
                  : new Date(year, month).toLocaleString(locale, {
                      month: 'long',
                      year: 'numeric',
                    })}
              </CalendarHeaderValue>
              <SvgWrapper onClick={handleNextClick}>
                <SvgIcon icon={chevronRightIcon} color={theme.main?.textColor} size={13} />
              </SvgWrapper>
            </MonthYear>
          </td>
        </tr>
        <tr>
          {firstWeek.map(date => (
            <td key={date.getTime()}>
              <CalendarHeaderValue>
                {date.toLocaleString(locale, {
                  weekday: 'short',
                  timeZone: utc ? 'UTC' : undefined,
                })}
              </CalendarHeaderValue>
            </td>
          ))}
        </tr>
      </CalendarHeader>
      <tbody>
        {weeks.map(week => (
          <tr key={`week-${week[0]?.getTime()}`}>
            {week.map(date => (
              <CalendarCellWrapper
                key={date.getTime()}
                data-ts={date.getTime()}
                onClick={handleDayClick}
              >
                {renderCell(date)}
              </CalendarCellWrapper>
            ))}
          </tr>
        ))}
      </tbody>
    </CalendarTable>
  );
};

export const Calendar = withThemeOverride(_Calendar);
Calendar.displayName = 'Calendar';

const CalendarTable = styled.table`
  border-collapse: collapse;
  table-layout: fixed;
  user-select: none;
  -ms-user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
`;

const CalendarHeader = styled.thead`
  ${p => optional('background-color', p.theme.main?.backgroundColor)}
  ${p => optional('color', p.theme.main?.textColor)}
  user-select: none;
`;

const MonthYear = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 19px;
  svg {
    cursor: pointer;
  }
`;

const SvgWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 36px;
  cursor: pointer;
  &:hover {
    background-color: ${p => p.theme.main?.highlight2};
  }
`;

const CalendarHeaderValue = styled.div`
  text-transform: capitalize;
  text-align: center;
  padding: 6px 0;
`;

const CalendarCellWrapper = styled.td`
  width: ${100 / DAYS_IN_WEEK}%;
`;

const CalendarCell = styled.div<{
  $disabled: boolean;
  $highlighted: boolean;
  $denseUi: boolean;
}>`
  display: flex;
  flex-direction: column;
  width: ${p => (p.$denseUi ? 40 : 48)}px;
  height: ${p => (p.$denseUi ? 44 : 52)}px;
  border-radius: 6px;
  margin: ${p => (p.$denseUi ? 2 : 3)}px;
  cursor: ${p => (p.$disabled ? 'default' : 'pointer')};
  pointer-events: ${p => (p.$disabled ? 'none' : 'inherit')};
  background: ${p => p.theme.main?.highlight2};
  border: solid 2px ${p => (p.$highlighted ? p.theme.main?.accentColor : p.theme.main?.highlight3)};
  ${p =>
    !p.$disabled &&
    `&:hover {
        border: solid 2px ${p.theme.main?.accentColor};
        color: ${p.theme.main?.accentColor};
    }`}
`;

const CalendarCellDay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
`;
const CalendarCellDayNumber = styled.div<{
  $today: boolean;
  $denseUi: boolean;
}>`
  width: ${p => (p.$denseUi ? 28 : 32)}px;
  height: ${p => (p.$denseUi ? 28 : 32)}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  ${p =>
    p.$today
      ? `background-color: ${p.theme.main?.accentColor}aa; color: ${p.theme.main?.accentTextColor};`
      : `background-color: ${p.theme.main?.highlight3}; color: ${p.theme.main?.textColor};`}
`;
